import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { IIssueWorkLog, ITimesheetProjectApproval, ITimesheetTaskRow } from '@pulse/shared';
import { Box, ButtonBase, Divider, IconButton, Paper, Stack, Typography } from '@pulse/ui';
import { useState } from 'react';

import { LINE_CLAMP_2_SX, TruncatableText } from '../../../components/TruncatableText';
import { cellTotal, dayColumnTotal, formatTotalHours, groupTaskRowsByProject, parseLocalDate, projectDayTotal, projectTotal, taskRowTotal } from '../utils/timesheetGrid';

import { TimesheetApprovalIndicator } from './TimesheetApprovalIndicator';

interface TimesheetMobileListProps {
  taskRows: ITimesheetTaskRow[];
  workLogs: IIssueWorkLog[];
  days: string[];
  selectedDay: string;
  onSelectDay: (dateIso: string) => void;
  selectedCell: { issueId: string; dateIso: string } | null;
  /** Opens the work log dialog for this (task, day) cell. */
  onCellClick: (issueId: string, dateIso: string) => void;
  projectApprovals?: ITimesheetProjectApproval[];
  onSubmitProject?: (projectId: string) => void;
  isSubmitting?: boolean;
}

function dayWeekdayLabel(dateIso: string): string {
  return parseLocalDate(dateIso).toLocaleDateString('en-US', { weekday: 'short' });
}

function dayNumberLabel(dateIso: string): string {
  return String(parseLocalDate(dateIso).getDate());
}

function dayFullLabel(dateIso: string): string {
  return parseLocalDate(dateIso).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// Mobile counterpart to TimesheetGrid: a day-strip selector plus, for the selected day only, a
// collapsible project-grouped card list (project header + one card per task, hours shown as a
// tappable button), ending in a Day Totals summary — the same information the desktop grid
// shows (per-project day/week subtotals, a day-totals row, collapsible groups), just laid out
// for one day at a time instead of all 7 day columns at once, since that table doesn't fit a
// phone width. Tapping a task's hours button opens the same WorkLogPanel content as the desktop
// side panel, but as a Dialog (see TimesheetPage).
export function TimesheetMobileList({
  taskRows,
  workLogs,
  days,
  selectedDay,
  onSelectDay,
  selectedCell,
  onCellClick,
  projectApprovals = [],
  onSubmitProject,
  isSubmitting,
}: TimesheetMobileListProps) {
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());
  const projectGroups = groupTaskRowsByProject(taskRows);
  const approvalByProject = new Map(projectApprovals.map((a) => [a.projectId, a]));

  if (taskRows.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No tasks assigned or logged this week.</Typography>;
  }

  function toggleCollapsed(projectId: string) {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
        {days.map((d) => {
          const isSelected = d === selectedDay;
          return (
            <ButtonBase
              key={d}
              onClick={() => onSelectDay(d)}
              sx={{
                flexShrink: 0,
                width: 48,
                py: 1,
                borderRadius: 2,
                border: 1,
                borderColor: isSelected ? 'primary.main' : 'divider',
                bgcolor: isSelected ? 'primary.main' : 'transparent',
                color: isSelected ? 'primary.contrastText' : 'text.primary',
              }}
            >
              <Stack alignItems="center" spacing={0.25}>
                <Typography variant="caption" fontWeight={600}>{dayWeekdayLabel(d)}</Typography>
                <Typography variant="body2" fontWeight={700}>{dayNumberLabel(d)}</Typography>
              </Stack>
            </ButtonBase>
          );
        })}
      </Stack>

      {projectGroups.map((group) => {
        const issueIds = group.tasks.map((t) => t.issueId);
        const approval = approvalByProject.get(group.projectId);
        const isCollapsed = collapsedProjects.has(group.projectId);
        const dayTotal = projectDayTotal(workLogs, issueIds, selectedDay);
        const weekTotal = projectTotal(workLogs, issueIds);
        return (
          <Box key={group.projectId}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <IconButton size="small" onClick={() => toggleCollapsed(group.projectId)} aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
                  {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>{group.projectName}</Typography>
                <TimesheetApprovalIndicator approval={approval} projectId={group.projectId} onSubmitProject={onSubmitProject} isSubmitting={isSubmitting} />
              </Stack>
              <Box sx={{ flexShrink: 0, ml: 1, textAlign: 'right' }}>
                <Typography variant="body2" fontWeight={700}>{formatTotalHours(dayTotal)}h</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{formatTotalHours(weekTotal)}h this week</Typography>
              </Box>
            </Stack>

            {!isCollapsed && (
              <Stack spacing={1}>
                {group.tasks.map((task) => {
                  const hours = cellTotal(workLogs, task.issueId, selectedDay);
                  const isSelected = selectedCell?.issueId === task.issueId && selectedCell?.dateIso === selectedDay;
                  return (
                    <Paper key={task.issueId} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <TruncatableText
                            text={`${task.issueKey} — ${task.issueName}`}
                            variant="body2"
                            fontWeight={600}
                            placement="top"
                            sx={LINE_CLAMP_2_SX}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatTotalHours(taskRowTotal(workLogs, task.issueId))}h this week
                          </Typography>
                        </Box>
                        <ButtonBase
                          onClick={() => onCellClick(task.issueId, selectedDay)}
                          aria-label={`Log hours for ${task.issueKey} on ${selectedDay}`}
                          sx={{
                            flexShrink: 0,
                            width: 56,
                            py: 1,
                            borderRadius: 1.5,
                            border: 1,
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            bgcolor: isSelected ? 'primary.main' : 'action.hover',
                            color: isSelected ? 'primary.contrastText' : 'text.primary',
                          }}
                        >
                          <Typography variant="body2" fontWeight={700}>{hours > 0 ? hours : '—'}</Typography>
                        </ButtonBase>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        );
      })}

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={700}>Total — {dayFullLabel(selectedDay)}</Typography>
        <Typography variant="body2" fontWeight={700}>{formatTotalHours(dayColumnTotal(workLogs, selectedDay))}h</Typography>
      </Stack>
    </Stack>
  );
}
